/**
 * Localizes a FRENCH pre-formatted date string ("Mardi 12 mai 2026", "28 février 2026")
 * coming from baked fragment/record data. The projector formats dates in French at
 * deploy time; on non-French pages the renderer re-expresses the same date via Intl
 * so weekday/month names follow the page language. Anything that doesn't parse as a
 * French date (ranges, free text, already-localized values) passes through unchanged.
 */

const FR_MONTH_INDEX: Record<string, number> = {
  janvier: 0,
  février: 1,
  fevrier: 1,
  mars: 2,
  avril: 3,
  mai: 4,
  juin: 5,
  juillet: 6,
  août: 7,
  aout: 7,
  septembre: 8,
  octobre: 9,
  novembre: 10,
  décembre: 11,
  decembre: 11,
};

const FR_WEEKDAYS = new Set(["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"]);

function bcp47(locale: string): string {
  return locale === "en" ? "en-GB" : locale;
}

export function localizeFrenchDateString(value: string | undefined | null, locale: string): string {
  const input = (value ?? "").trim();
  if (!input || locale === "fr") return input;

  // "(Mardi )12 mai 2026" — weekday optional; anything else (ranges, partials) passes through.
  const m = input.match(/^(?:([A-Za-zÀ-ÿ]+)\s+)?(\d{1,2})\s+([A-Za-zÀ-ÿ]+)\s+(\d{4})$/);
  if (!m) return input;
  const [, weekday, day, monthName, year] = m;
  if (weekday && !FR_WEEKDAYS.has(weekday.toLowerCase())) return input;
  const monthIndex = FR_MONTH_INDEX[monthName.toLowerCase()];
  if (monthIndex === undefined) return input;

  try {
    const date = new Date(Date.UTC(Number(year), monthIndex, Number(day)));
    const formatted = new Intl.DateTimeFormat(bcp47(locale), {
      ...(weekday ? { weekday: "long" } : {}),
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
    // Match the French source's capitalized weekday ("Mardi 12 mai" -> "Tuesday 12 May").
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  } catch {
    return input;
  }
}
